import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ArrowCircleDownIcon from '../Icons/ArrowCircleDownIcon';
import ArrowCircleUpIcon from '../Icons/ArrowCircleUpIcon';
import ArrowsExpandIcon from '../Icons/ArrowsExpandIcon';
import CloudDownloadIcon from '../Icons/CloudDownloadIcon';
import PdfIcon from '../Icons/pdfIcon';
import ZoomInIcon from '../Icons/ZoomInIcon';
import ZoomOutIcon from '../Icons/ZoomOutIcon';
import style from './PDFPreview.module.scss';

// Use CDN for PDF.js library
const PDF_JS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDF_WORKER_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

// Config for PDF Preview
const PDF_PREVIEW_CONFIG = {
    initialScale: 1.0,
    initialPageNumber: 1,
    defaultDevicePixelRatio: 1,
    minScale: 0.5,
    maxScale: 3.0,
    contentPadding: 40,
};

const PDFPreview = ({ title, cta, pdfPageOrientation = '' }) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(PDF_PREVIEW_CONFIG.initialPageNumber);
    const [scale, setScale] = useState(PDF_PREVIEW_CONFIG.initialScale);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isRendering, setIsRendering] = useState(false);
    const zoomTimeoutRef = useRef(null);

    // Helper to calculate scale based on different modes and constraints
    const calculateScaleForDimensions = useCallback(async ({ pdf, pageNum, type = 'widthFit' }) => {
        if (!pdf || !containerRef.current) return PDF_PREVIEW_CONFIG.initialScale;
        const containerElement = containerRef.current;
        try {
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 1.0 });
            let optimalScale;

            if (type === 'fullscreen') {
                const headerHeight = 52;
                const availableHeight = window.innerHeight - headerHeight - PDF_PREVIEW_CONFIG.contentPadding;
                const availableWidth = window.innerWidth - PDF_PREVIEW_CONFIG.contentPadding;
                const scaleX = availableWidth / viewport.width;
                const scaleY = availableHeight / viewport.height;
                optimalScale = Math.min(scaleX, scaleY, PDF_PREVIEW_CONFIG.maxScale);
            } else {
                const containerWidth = containerElement.clientWidth || containerElement.offsetWidth;
                const availableWidth = containerWidth - PDF_PREVIEW_CONFIG.contentPadding;
                // Use only pdfPageOrientation to determine scaling strategy
                if (pdfPageOrientation === 'Landscape') {
                    // Landscape orientation - consider both width and height constraints
                    const containerHeight = containerElement.clientHeight || containerElement.offsetHeight;
                    const availableHeight = containerHeight - PDF_PREVIEW_CONFIG.contentPadding;
                    const scaleByWidth = availableWidth / viewport.width;
                    const scaleByHeight = availableHeight / viewport.height;
                    optimalScale = Math.min(scaleByWidth, scaleByHeight);
                } else {
                    // Portrait orientation or empty - use width-based scaling
                    optimalScale = availableWidth / viewport.width;
                }
                optimalScale = Math.min(optimalScale, PDF_PREVIEW_CONFIG.maxScale);
            }
            return Math.max(optimalScale, PDF_PREVIEW_CONFIG.minScale);
        } catch (error) {
            return PDF_PREVIEW_CONFIG.initialScale;
        }
    }, [pdfPageOrientation]);

    // Load PDF.js script and PDF document
    useEffect(() => {
        let isMounted = true;
        let loadingDocumentTask = null;
        const cleanup = () => {
            if (loadingDocumentTask && loadingDocumentTask.destroy) {
                loadingDocumentTask.destroy();
            }
        };
        // Handle load PDF.js script library & worker
        const loadPDFJSLibrary = () => {
            return new Promise((resolve, reject) => {
                if (typeof window === 'undefined') return reject(new Error('Window is undefined'));
                if (window.pdfjsLib) return resolve(window.pdfjsLib);
                const script = document.createElement('script');
                script.src = PDF_JS_CDN;
                script.async = true;
                script.onload = () => {
                    if (window.pdfjsLib) {
                        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_CDN;
                        resolve(window.pdfjsLib);
                    } else {
                        reject(new Error('PDF.js library not loaded'));
                    }
                };
                script.onerror = reject;
                document.head.appendChild(script);
            });
        };
        // Handle load PDF document file
        const loadGeneralPDF = async () => {
            if (!cta?.link?.url) return;
            setIsLoading(true);
            try {
                const pdfjsLib = await loadPDFJSLibrary();
                loadingDocumentTask = pdfjsLib.getDocument(cta?.link?.url);
                const pdf = await loadingDocumentTask.promise;
                if (!isMounted) return;
                setPdfDoc(pdf);
                setNumPages(pdf.numPages);
                setTimeout(async () => {
                    if (!isMounted) return;
                    const initialScale = await calculateScaleForDimensions({
                        pdf,
                        pageNum: PDF_PREVIEW_CONFIG.initialPageNumber,
                        type: 'widthFit',
                    });
                    setScale(initialScale);
                }, 100);
            } catch (error) {
                if (!isMounted) return;
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };
        // Load PDF (library and document)
        loadGeneralPDF();
        // Cleanup
        return () => {
            isMounted = false;
            cleanup();
        };
    }, [cta?.link?.url]);

    // Render the current page with dynamic canvas creation
    const renderPage = useCallback(async () => {
        if (!pdfDoc || !canvasRef.current) return;
        setIsRendering(true);
        try {
            const page = await pdfDoc.getPage(pageNumber);
            const viewport = page.getViewport({ scale: scale });
            const outputScale = PDF_PREVIEW_CONFIG.defaultDevicePixelRatio || window.devicePixelRatio;
            const newCanvas = document.createElement('canvas');
            const context = newCanvas.getContext('2d');
            newCanvas.width = viewport.width;
            newCanvas.height = viewport.height;
            newCanvas.style.width = `${Math.floor(viewport.width)}px`;
            newCanvas.style.height = `${Math.floor(viewport.height)}px`;
            newCanvas.className = canvasRef.current.className;
            const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;
            const renderContext = {
                canvasContext: context,
                transform: transform,
                viewport: viewport,
            };
            const renderTask = page.render(renderContext);
            await renderTask.promise;
            const oldCanvas = canvasRef.current;
            if (oldCanvas && oldCanvas.parentNode && newCanvas) {
                const parent = oldCanvas.parentNode;
                parent.replaceChild(newCanvas, oldCanvas);
                canvasRef.current = newCanvas;
                newCanvas.style.display = 'block';
            }
        } catch (error) {
            if (error.name !== 'RenderingCancelledException') {
                setIsError(true);
            }
        } finally {
            setIsRendering(false);
        }
    }, [pdfDoc, pageNumber, scale]);

    // Initial render pdf page with optimized debouncing for zoom
    useEffect(() => {
        if (pdfDoc) {
            if (zoomTimeoutRef.current) {
                clearTimeout(zoomTimeoutRef.current);
            }
            zoomTimeoutRef.current = setTimeout(() => {
                renderPage();
            }, 100);
        }
        return () => {
            if (zoomTimeoutRef.current) {
                clearTimeout(zoomTimeoutRef.current);
            }
        };
    }, [pdfDoc, renderPage, scale]);

    // Helper to calculate scale based on mode type (widthFit or fullscreen)
    const calculateScale = useCallback(
        async (type = 'widthFit') => {
            if (!pdfDoc || !containerRef.current) return;
            if (type === 'widthFit' && isFullscreen) return;
            try {
                const newScale = await calculateScaleForDimensions({
                    pdf: pdfDoc,
                    pageNum: pageNumber,
                    type: type,
                });
                setScale(newScale);
            } catch (error) {
                setScale(1.0);
            }
        },
        [pdfDoc, pageNumber, isFullscreen, calculateScaleForDimensions]
    );

    // Recalculate widthFit scale when page changes (only in normal mode)
    useEffect(() => {
        if (pdfDoc && !isFullscreen && !isLoading) {
            calculateScale('widthFit');
        }
    }, [pdfDoc, pageNumber, calculateScale, isFullscreen]);

    // Handle back to previous page
    const handlePrevPage = useCallback(() => {
        setPageNumber((prev) => Math.max(prev - 1, 1));
    }, []);

    // Handle go to next page
    const handleNextPage = useCallback(() => {
        setPageNumber((prev) => (numPages ? Math.min(prev + 1, numPages) : prev));
    }, [numPages]);

    // Handle page input change
    const handlePageInputChange = useCallback(
        (e) => {
            const page = parseInt(e.target.value, 10);
            if (!isNaN(page) && page >= 1 && page <= numPages) {
                setPageNumber(page);
            }
        },
        [pageNumber, numPages]
    );

    // Helper to handle zoom with smooth behavior for both modes
    const handleZoom = useCallback(
        (direction) => {
            const container = containerRef.current;
            if (!container || isRendering) return;
            setScale((prevScale) => {
                const newScale = direction === 'in' ? Math.min(prevScale + 0.25, 3.0) : Math.max(prevScale - 0.25, 0.5);
                return newScale;
            });
        },
        [isFullscreen, isRendering]
    );

    // Handle zoom in/out
    const handleZoomIn = useCallback(() => handleZoom('in'), [handleZoom]);
    const handleZoomOut = useCallback(() => handleZoom('out'), [handleZoom]);

    // Handle Download Pdf
    const handleDownload = useCallback(async () => {
        if (!cta?.link?.url) return;
        try {
            // Fetch the PDF as blob
            const response = await fetch(cta.link.url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const blob = await response.blob();
            // Create blob URL and download
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = title || 'document.pdf';
            // Append to body, click, and cleanup
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            // Clean up the blob URL
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            // Fallback to opening in new tab if download fails
            window.open(cta.link.url, '_blank');
        }
    }, [cta?.link?.url, title]);

    // Handle fullscreen toggle
    const handleFullscreen = useCallback(() => {
        setIsFullscreen((prev) => {
            const newFullscreenState = !prev;
            if (newFullscreenState) {
                document.documentElement.classList.add('pdf-fullscreen');
                requestAnimationFrame(() => {
                    calculateScale('fullscreen');
                });
            } else {
                document.documentElement.classList.remove('pdf-fullscreen');
                requestAnimationFrame(() => {
                    calculateScale('widthFit');
                });
            }
            return newFullscreenState;
        });
    }, [calculateScale]);

    // Force re-render canvas when fullscreen state changes
    useEffect(() => {
        if (pdfDoc && canvasRef.current && isFullscreen !== null) {
            renderPage(pageNumber);
        }
    }, [isFullscreen, pdfDoc, pageNumber, renderPage]);

    // Cleanup fullscreen state on unmount
    useEffect(() => {
        return () => {
            document.documentElement.classList.remove('pdf-fullscreen');
        };
    }, []);

    // Early return if no URL
    if (!cta?.link?.url) {
        return (
            <div className={`${style.pdfPreview}`}>
                <div className={style.error}>
                    <p>No PDF URL provided</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`${style.pdfPreview} ${isFullscreen ? style.fullscreen : ''}`}>
            {/* CLOSE BUTTON FOR FULLSCREEN */}
            {isFullscreen && (
                <button onClick={handleFullscreen} className={style.closeButton} type="button" title="Exit Fullscreen">
                    ×
                </button>
            )}
            {/* HEADER */}
            <div className={style.header}>
                <div className={style.title}>{title}</div>
                <div className={style.icon}>
                    <PdfIcon />
                </div>
            </div>

            {/* DOCUMENT */}
            <div className={style.previewDocument} ref={containerRef}>
                {isError && (
                    <div className={style.error}>
                        <p>Failed to load PDF. Please try again.</p>
                    </div>
                )}
                <div className={style.canvasContainer}>
                    <canvas
                        ref={canvasRef}
                        className={`${style.canvasElement} ${isLoading || isError ? style.hidden : style.visible}`}
                    />
                </div>
                {isLoading && (
                    <div className={style.loading}>
                        <div className={style.content}>
                            <div className={style.spinner}></div>
                            <p>Loading PDF...</p>
                        </div>
                    </div>
                )}
            </div>

            {/* CONTROLS */}
            {numPages && (
                <div className={style.controls}>
                    {/* Zoom Controls */}
                    <div className={style.groupControls}>
                        <button
                            onClick={handleZoomOut}
                            className={style.controlButton}
                            type="button"
                            title="Zoom Out"
                            disabled={scale <= 0.5}
                        >
                            <ZoomOutIcon />
                        </button>
                        <button
                            onClick={handleZoomIn}
                            className={style.controlButton}
                            type="button"
                            title="Zoom In"
                            disabled={scale >= 3.0}
                        >
                            <ZoomInIcon />
                        </button>
                    </div>
                    {/* Pagination Controls */}
                    <div className={style.paginationControls}>
                        <button
                            onClick={handlePrevPage}
                            disabled={pageNumber <= 1}
                            type="button"
                            title="Previous Page"
                            className={style.controlButton}
                        >
                            <ArrowCircleUpIcon />
                        </button>
                        <input
                            type="number"
                            min="1"
                            max={numPages}
                            value={pageNumber}
                            onChange={handlePageInputChange}
                            className={style.pageInput}
                        />
                        <div> / </div>
                        <div className={style.pageTotal}> {numPages || 0}</div>
                        <button
                            onClick={handleNextPage}
                            disabled={pageNumber >= numPages}
                            className={style.controlButton}
                            type="button"
                            title="Next Page"
                        >
                            <ArrowCircleDownIcon />
                        </button>
                    </div>
                    {/* Expand/Download/Print Controls */}
                    <div className={style.groupControls}>
                        <button
                            onClick={handleFullscreen}
                            className={style.controlButton}
                            type="button"
                            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                        >
                            <ArrowsExpandIcon />
                        </button>
                        <button
                            onClick={handleDownload}
                            className={style.controlButton}
                            type="button"
                            title="Download PDF"
                        >
                            <CloudDownloadIcon />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

PDFPreview.propTypes = {
    image: PropTypes.string,
    mobileImage: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    cta: PropTypes.string,
    pdfPageOrientation: PropTypes.oneOf(['', 'Portrait', 'Landscape'])
};

export default PDFPreview;
